import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
  ExternalLink, Handshake, X, Check, Landmark, Briefcase, Home, Car, Activity, ShieldCheck, Receipt,
} from "lucide-react";

const INPUT_CLS = "w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-colors";

const CATEGORY_ICONS = {
  personal_loans: Landmark,
  business_funding: Briefcase,
  mortgage: Home,
  auto_financing: Car,
  credit_monitoring: Activity,
  insurance: ShieldCheck,
  tax_services: Receipt,
};

function IntroModal({ partner, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim()) { setError("Name and email are required."); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/${partner.id}/lead`, form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't send your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="marketplace-intro-modal">
      <div className="card-dark w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="marketplace-intro-close">
          <X className="w-5 h-5" />
        </button>
        {done ? (
          <div className="text-center py-6" data-testid="marketplace-intro-success">
            <div className="w-12 h-12 rounded-full bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/40 flex items-center justify-center mx-auto mb-4">
              <Check className="w-6 h-6 text-[var(--if-gold)]" />
            </div>
            <h3 className="font-display text-2xl text-white mb-2">Request received.</h3>
            <p className="text-sm text-white/65" style={{ fontFamily: "Inter, sans-serif" }}>
              Our team will personally connect you with {partner.name} within one business day.
            </p>
          </div>
        ) : (
          <>
            <div className="font-mono-label text-[var(--if-gold)] mb-2">{partner.category_label}</div>
            <h3 className="font-display text-2xl text-white mb-1">Request an intro</h3>
            <p className="text-sm text-white/60 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
              Tell us how to reach you — we'll broker the connection to <strong className="text-white">{partner.name}</strong>.
            </p>
            <form onSubmit={submit} className="flex flex-col gap-3">
              <input className={INPUT_CLS} placeholder="Your name *" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="marketplace-lead-name" />
              <input className={INPUT_CLS} type="email" placeholder="Email *" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="marketplace-lead-email" />
              <input className={INPUT_CLS} placeholder="Phone (optional)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="marketplace-lead-phone" />
              <textarea className={INPUT_CLS} rows={3} placeholder="What are you looking for? (optional)" value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="marketplace-lead-message" />
              {error && <p className="text-xs text-red-400" data-testid="marketplace-lead-error">{error}</p>}
              <button type="submit" disabled={busy} className="btn-gold justify-center disabled:opacity-60" data-testid="marketplace-lead-submit">
                {busy ? "Sending…" : "Request Intro"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function PartnerCard({ partner, onIntro }) {
  const [opening, setOpening] = useState(false);
  const Icon = CATEGORY_ICONS[partner.category] || Handshake;

  const visit = async () => {
    setOpening(true);
    try {
      const { data } = await api.post(`/marketplace/${partner.id}/click`);
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      alert("This partner link isn't available right now.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="card-dark p-6 hover:border-[var(--if-gold)]/40 transition-all relative overflow-hidden group" data-testid={`marketplace-partner-${partner.id}`}>
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-[var(--if-gold)]/8 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="w-10 h-10 rounded-xl bg-[var(--if-gold)]/15 border border-[var(--if-gold)]/30 flex items-center justify-center">
            <Icon className="w-4.5 h-4.5 text-[var(--if-gold)]" />
          </div>
          <span className="text-[10px] uppercase tracking-[0.16em] text-white/40" style={{ fontFamily: "Inter, sans-serif" }}>
            {partner.category_label}
          </span>
        </div>
        <h3 className="font-display text-xl text-white mb-2">{partner.name}</h3>
        <p className="text-sm text-white/60 leading-relaxed mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
          {partner.description}
        </p>
        {partner.cta_type === "link" && partner.has_url ? (
          <button onClick={visit} disabled={opening} className="btn-gold !py-2 !px-4 !text-[12px] disabled:opacity-60" data-testid={`marketplace-visit-${partner.id}`}>
            <ExternalLink className="w-3.5 h-3.5" />
            {opening ? "Opening…" : "Visit Partner"}
          </button>
        ) : (
          <button onClick={() => onIntro(partner)} className="btn-outline !py-2 !px-4 !text-[12px]" data-testid={`marketplace-intro-${partner.id}`}>
            <Handshake className="w-3.5 h-3.5" />
            Request an Intro
          </button>
        )}
      </div>
    </div>
  );
}

export default function Marketplace() {
  const [partners, setPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [intro, setIntro] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/marketplace");
        setPartners(data.partners || []);
        setCategories(data.categories || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const usedCategories = categories.filter((c) => partners.some((p) => p.category === c.id));
  const shown = filter === "all" ? partners : partners.filter((p) => p.category === filter);

  return (
    <section className="section" data-testid="marketplace-page">
      <div className="container-if">
        <div className="max-w-2xl mb-10">
          <div className="font-mono-label text-[var(--if-gold)] mb-3">Partner Marketplace</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6">
            Beyond credit repair — <span className="italic text-[var(--if-gold)]">allied resources.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#1B211A]/70" style={{ fontFamily: "Inter, sans-serif" }}>
            Vetted partners for the next mission: funding, financing, protection, and taxes.
            Some connect instantly — for others, we broker a personal introduction.
          </p>
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto pb-1" data-testid="marketplace-filters">
          <button onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-full border text-xs whitespace-nowrap transition-all ${filter === "all" ? "bg-[var(--if-gold)] text-[#F7F5EF] border-[var(--if-gold)] font-semibold" : "border-[#1B211A]/20 text-[#1B211A]/70 hover:border-[var(--if-gold)]/60"}`}
            data-testid="marketplace-filter-all">
            All
          </button>
          {usedCategories.map((c) => (
            <button key={c.id} onClick={() => setFilter(c.id)}
              className={`px-4 py-2 rounded-full border text-xs whitespace-nowrap transition-all ${filter === c.id ? "bg-[var(--if-gold)] text-[#F7F5EF] border-[var(--if-gold)] font-semibold" : "border-[#1B211A]/20 text-[#1B211A]/70 hover:border-[var(--if-gold)]/60"}`}
              data-testid={`marketplace-filter-${c.id}`}>
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-[#1B211A]/50 py-12 text-center">Loading partners…</p>
        ) : shown.length === 0 ? (
          <p className="text-sm text-[#1B211A]/50 py-12 text-center" data-testid="marketplace-empty">No partners in this category yet — check back soon.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {shown.map((p) => <PartnerCard key={p.id} partner={p} onIntro={setIntro} />)}
          </div>
        )}

        <div className="card-dark p-6 mt-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-xl text-white mb-1">Want to become a partner?</h3>
            <p className="text-sm text-white/60" style={{ fontFamily: "Inter, sans-serif" }}>
              We refer ready-to-fund clients every week. Let's talk about a referral relationship.
            </p>
          </div>
          <Link to="/contact" className="btn-outline !py-2.5 !px-5 !text-[12px] shrink-0" data-testid="marketplace-become-partner">
            Contact Us
          </Link>
        </div>
      </div>
      {intro && <IntroModal partner={intro} onClose={() => setIntro(null)} />}
    </section>
  );
}
